import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, fetchProjects, fetchCategoryTerms, fetchGalleryBySlug } from '../lib/api';

export default function ProjectsPage() {
  const [pageData, setPageData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Lightbox State
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadInitialData() {
      try {
        const [page, cats, gall] = await Promise.all([
          fetchPageBySlug('projekter'),
          fetchCategoryTerms(),
          fetchGalleryBySlug('isoleringsarbejde-projekter')
        ]);
        const EXCLUDED_SLUGS = ['hulmursisolering', 'loftsisolering', 'loftisolering'];
        const filteredCats = (cats || []).filter(c => !EXCLUDED_SLUGS.includes(c.slug));
        setPageData(page);
        setCategories(filteredCats);
        setGallery(gall);
      } catch (err) {
        console.error('Error loading initial projects page data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadInitialData();
  }, []);

  // Fetch projects when category changes
  useEffect(() => {
    async function loadProjects() {
      setProjectsLoading(true);
      try {
        const projList = await fetchProjects(selectedCategory);
        setProjects(projList);
      } catch (err) {
        console.error('Error loading projects:', err);
      } finally {
        setProjectsLoading(false);
      }
    }
    loadProjects();
  }, [selectedCategory]);

  // Update document metadata
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || 'Projekter - Røjsmose Isolering ApS';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageData.meta_description || '');
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'description';
        newMeta.content = pageData.meta_description || '';
        document.head.appendChild(newMeta);
      }
    }
  }, [pageData]);

  // Lightbox Navigation
  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = (e) => {
    e.stopPropagation();
    if (gallery && gallery.images) {
      setLightboxIndex((prev) => (prev + 1) % gallery.images.length);
    }
  };
  const prevImage = (e) => {
    e.stopPropagation();
    if (gallery && gallery.images) {
      setLightboxIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="py-16 sm:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Page Header */}
        <div className="border-b border-slate-200 pb-8 mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl mt-2">
            {pageData?.title || 'Projekter'}
          </h1>
          {/* Exact Scraped Text Content */}
          <div
            className="mt-4 text-lg text-slate-600 prose prose-slate"
            dangerouslySetInnerHTML={{ __html: pageData?.content || '' }}
          />
        </div>

        {/* Category Filter Controls */}
        <div className="flex flex-wrap items-center gap-2 mb-10">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-5 py-2 text-sm font-semibold transition-all duration-200 ${
              selectedCategory === null
                ? 'bg-brand-orange text-white shadow-sm'
                : 'bg-slate-100 text-brand-navy hover:bg-slate-200'
            }`}
          >
            Alle projekter
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setSelectedCategory(cat.slug)}
              className={`px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                selectedCategory === cat.slug
                  ? 'bg-brand-orange text-white shadow-sm'
                  : 'bg-slate-100 text-brand-navy hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        {projectsLoading ? (
          <div className="flex min-h-[30vh] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-slate-300 rounded-xl bg-slate-50">
            <p className="text-slate-500">Ingen projekter fundet i denne kategori.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {projects.map((project) => {
              const projImage = project.images?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/rojsmose-hero-isolering.jpg';
              return (
                <Link
                  key={project.id}
                  to={`/projekter/${project.project_slug}`}
                  className="group flex flex-col overflow-hidden rounded-none border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <div className="relative h-56 overflow-hidden bg-slate-100 rounded-none">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${projImage.replace('https://assets.auxodev.dk/', '')}`}
                      alt={project.title}
                      className="h-full w-full object-cover rounded-none transition-transform duration-300 group-hover:scale-105"
                    />
                    {project.location && (
                      <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-none bg-brand-navy/80 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                        <i className="fa-solid fa-location-dot text-[10px]" aria-hidden="true" />
                        {project.location}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="text-lg font-bold text-brand-navy mb-2 group-hover:text-brand-orange transition-colors">
                      {project.title}
                    </h3>
                    <div
                      className="text-sm text-slate-600 leading-relaxed prose prose-slate max-w-none line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: project.description || '' }}
                    />
                    <div className="mt-auto pt-4 flex items-center gap-1 text-sm font-bold text-brand-orange">
                      Læs mere
                      <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Dynamic Gallery Section */}
        {gallery && gallery.images && gallery.images.length > 0 && (
          <div className="border-t border-slate-200 pt-16 mt-16">
            <h2 className="text-2xl font-extrabold tracking-tight text-brand-navy sm:text-3xl mb-2">
              {gallery.title}
            </h2>
            <p className="text-slate-600 mb-8">
              Klik på et billede for at åbne det i stor visning.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {gallery.images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="group relative aspect-square overflow-hidden rounded-none bg-slate-100 border border-slate-200 transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-brand-orange"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,height=300,fit=cover,quality=85,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`Galleri billede ${index + 1}`}
                    className="h-full w-full object-cover rounded-none"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-white text-xl" aria-hidden="true" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {lightboxIndex !== null && gallery && gallery.images && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 animate-in fade-in duration-200"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white hover:text-brand-orange text-3xl focus:outline-none p-2"
              aria-label="Luk visning"
            >
              <i className="fa-light fa-xmark" aria-hidden="true" />
            </button>

            {/* Prev Button */}
            <button
              onClick={prevImage}
              className="absolute left-6 text-white hover:text-brand-orange text-4xl focus:outline-none p-2 hidden sm:block"
              aria-label="Forrige billede"
            >
              <i className="fa-light fa-chevron-left" aria-hidden="true" />
            </button>

            {/* Image Container */}
            <div className="relative max-w-4xl max-h-[80vh] flex flex-col items-center justify-center">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${gallery.images[lightboxIndex].replace('https://assets.auxodev.dk/', '')}`}
                alt={`Billede ${lightboxIndex + 1}`}
                className="max-w-full max-h-[75vh] object-contain rounded-none shadow-2xl select-none"
                onClick={(e) => e.stopPropagation()}
              />
              <p className="text-white/70 text-sm mt-4 font-semibold">
                Billede {lightboxIndex + 1} af {gallery.images.length}
              </p>
            </div>

            {/* Next Button */}
            <button
              onClick={nextImage}
              className="absolute right-6 text-white hover:text-brand-orange text-4xl focus:outline-none p-2 hidden sm:block"
              aria-label="Næste billede"
            >
              <i className="fa-light fa-chevron-right" aria-hidden="true" />
            </button>

            {/* Mobile Swipe/Tap Indicator */}
            <div className="absolute bottom-6 left-0 right-0 text-center text-white/40 text-xs sm:hidden">
              Tryk til højre/venstre for at navigere
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
